import React from 'react'
import {
  Box,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material'
import { CollapsableRow, Column, Row } from '../../../store/types/utils/DataTableUtils'
import { Order } from '../../../store/types/components/table/order'
import { StyledTableSortLabel } from '../../../styles/TableStyles'
import CollapsibleTableRow from './CollapsibleTableRow'
import { footerCellSx, StyledHeaderCell } from '../../../styles/WorklogTableStyles'

export type SortFields = {
  order: Order
  orderBy: string
  nestedOrderBy: string
}

type CollapsibleTableProps = {
  columns: Column[]
  collapsableRows: CollapsableRow[]
  renderRow: (row: CollapsableRow) => React.ReactNode
  renderCollapsedRow: (row: Row, open: boolean) => React.ReactNode
  renderFooter?: () => React.ReactNode
  sortFunction?: (property: string) => (event: React.MouseEvent<unknown>) => void
  sortFields?: SortFields
  noRowsText?: string
  fullHeight?: boolean
  tableContainerSxProps?: SxProps<Theme>
}

const CollapsibleTable = ({
  columns,
  collapsableRows,
  renderRow,
  renderCollapsedRow,
  renderFooter,
  sortFunction,
  sortFields,
  noRowsText,
  fullHeight,
  tableContainerSxProps,
}: CollapsibleTableProps) => {
  const boxSx: SxProps<Theme> = {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid',
    borderColor: 'background.elevation8',
    ...(fullHeight && { display: 'flex', flexDirection: 'column', height: '100%' }),
  }

  const containerSx: SxProps<Theme> = {
    minHeight: 0,
    border: 0,
    borderRadius: 0,
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '@supports (-moz-appearance:none)': { scrollbarWidth: 'none' },
    position: 'relative',
    ...(fullHeight ? { flex: 1, overflowY: 'scroll' } : { maxHeight: 400, overflowY: 'auto' }),
    ...tableContainerSxProps,
  }

  const emptyRows = Math.max(0, 5 - collapsableRows.length)

  const table = (
    <Table stickyHeader>
      <TableHead
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <TableRow
          sx={{
            height: '50px',
            '@supports (-moz-appearance:none)': {
              '.MuiTableCell-root': { height: '36px', minHeight: '36px' },
            },
          }}
        >
          <StyledHeaderCell
            sx={{
              width: { xs: '35px', sm: '48px' },
              p: 0,
              minWidth: { xs: '35px', sm: '48px' },
              height: '100%',
              backgroundColor: 'background.elevation2',
            }}
            component={'td'}
          />
          {columns.map((column, index) => {
            const isFirstCell = index === 0
            return (
              <StyledHeaderCell
                key={column.id}
                align={!isFirstCell ? 'center' : 'left'}
                component={column.label.length > 0 ? 'th' : 'td'}
                sx={{
                  py: 1,
                  px: 1,
                  pl: isFirstCell ? 0 : 1,
                  whiteSpace: 'normal',
                  height: '100%',
                  minWidth: isFirstCell ? 100 : 'auto',
                  backgroundColor: 'background.elevation2',
                }}
              >
                {sortFunction && sortFields ? (
                  <StyledTableSortLabel
                    active={
                      sortFields.orderBy === column.id ||
                      (column.id.includes(sortFields.nestedOrderBy) && sortFields.nestedOrderBy.length !== 0)
                    }
                    direction={
                      sortFields.orderBy === column.id || column.id.includes(sortFields.nestedOrderBy)
                        ? sortFields.order
                        : 'asc'
                    }
                    onClick={sortFunction(column.id)}
                  >
                    {column.label}
                  </StyledTableSortLabel>
                ) : (
                  column.label
                )}
              </StyledHeaderCell>
            )
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {collapsableRows.length > 0 &&
          collapsableRows.map(row => (
            <CollapsibleTableRow
              key={row.id || row.index}
              data={row}
              renderRow={renderRow}
              renderCollapsedRow={renderCollapsedRow}
              hasCollapsedRows={row.hasCollapsedData}
            />
          ))}
        {emptyRows > 0 && (
          <TableRow style={{ height: 53 * emptyRows }}>
            <TableCell colSpan={columns.length + 1} />
          </TableRow>
        )}
      </TableBody>
      {!fullHeight && renderFooter && (
        <TableFooter
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            '& .MuiTableCell-root': footerCellSx,
          }}
        >
          {renderFooter()}
        </TableFooter>
      )}
    </Table>
  )

  return (
    <Box sx={boxSx}>
      <TableContainer sx={containerSx}>
        {table}
        {collapsableRows.length === 0 && noRowsText && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {noRowsText}
          </Box>
        )}
      </TableContainer>
      {fullHeight && renderFooter && (
        <Box>
          <Table>
            <TableFooter
              sx={{
                '& .MuiTableCell-root': footerCellSx,
              }}
            >
              {renderFooter()}
            </TableFooter>
          </Table>
        </Box>
      )}
    </Box>
  )
}

export default CollapsibleTable
